import React, { useState, useContext, useRef } from 'react'
import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import type { StyleClasses } from '../../styles'
import FormControl from '@material-ui/core/FormControl'
import clone from 'lodash/clone'
import find from 'lodash/find'
import isEqual from 'lodash/isEqual'
import type { EmailTemplate, OptionsDocument } from 'paintscout'
import { getFeature, getObjectLabels, getQuoteTypeOptions } from '@paintscout/util/builder'
import Button from '../../components/Button'
import Checkbox from '../../components/Checkbox'
import ConfirmationDialog from '../../dialogs/ConfirmationDialog'
import type { DialogProps } from '../../components/Dialog'
import Dialog, { DialogActions, DialogContent, DialogTitle } from '../../components/Dialog'
import FormControlLabel from '../../components/FormControlLabel'
import Grid from '../../components/Grid'
import InputField from '../../components/InputField'
import { Editor } from '../../components/Editor'
import ImportJsonDialog from '../../components/ImportJsonDialog'
import ExportJsonDialog from '../../components/ExportJsonDialog'
import DialogStackContext from '../../providers/DialogStack/DialogStackContext'
import { getTemplateContext, getTemplateTags } from '@paintscout/util/util'
import { useUser } from '../../providers/UserProvider'
import MenuItem from '../../components/MenuItem'
import SplitMenuButton from '../../components/SplitMenuButton'
import ArrowIcon from '@material-ui/icons/ArrowDropDown'
import CloseIcon from '@material-ui/icons/Close'
import IconButton from '../../components/IconButton'
import ActiveSwitch from '../../components/ActiveSwitch'
import { useClientOptions } from '../../providers/ClientOptionsProvider'
import type { DropdownSelectOption } from '../../components/DropdownSelect'
import DropdownSelect from '../../components/DropdownSelect'
import uniqBy from 'lodash/uniqBy'

const useStyles = makeStyles<Theme>((theme) => ({
  root: {},
  expansion: {
    borderRadius: theme.typography.pxToRem(0),
    border: `0.15rem solid ${theme.palette.text.primary}`
  },
  expansionSummary: {
    backgroundColor: theme.palette.text.primary,
    '& span': {
      color: 'white'
    }
  },
  templateField: {
    marginTop: theme.typography.pxToRem(20)
  },
  chipInput: {
    minHeight: theme.typography.pxToRem(0),
    '& $input': {
      minHeight: theme.typography.pxToRem(0)
    }
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  rightContentWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  switchLabel: {
    marginLeft: '0 !important',
    marginRight: 0
  }
}))

export interface EmailTemplateDialogProps extends DialogProps {
  classes?: StyleClasses<typeof useStyles>
  item: EmailTemplate
  customTemplate?: boolean
  addressBook?: any
  options?: OptionsDocument
  isAdmin?: boolean
  toHidden?: boolean
  onConfirm?: (updatedTemplate: EmailTemplate) => void
  onCancel?: () => void
  onEmailMigrate?: (selectedTemplate: EmailTemplate) => void
}

function EmailTemplateDialog(props: EmailTemplateDialogProps) {
  const classes = useStyles(props)
  const {
    loading,
    item,
    onCancel,
    onConfirm,
    customTemplate,
    addressBook,
    options,
    isAdmin: isAdminSite,
    toHidden,
    onEmailMigrate,
    ...otherProps
  } = props

  const { preferences, isAdmin: isAdminUser, isCorporate } = useUser()
  const editorPreference = preferences?.editor ?? 'always'
  const emailSubjectRef = useRef(null)
  const emailBodyRef = useRef(null)
  const hasProfileCommunications = !getFeature({
    options,
    preferences,
    path: 'settingsCommunications.enabled'
  })

  const { isAdmin } = useClientOptions()
  const canEdit = isAdminUser || isCorporate || hasProfileCommunications || isAdmin

  const [template, setTemplate] = useState(clone(item))
  const initialTemplate = React.useRef(clone(item))
  const { openDialog, dismissDialog } = useContext(DialogStackContext)
  /**
   * Set template.additionalRecipients form for Selections
   * with value and label.
   */
  const recipientsList = (template?.additionalRecipients || []).map((email) => {
    return { value: email, label: email }
  })
  const [recipients, setRecipients] = useState(recipientsList)

  const initialAddressBookList: DropdownSelectOption[] = (addressBook ?? []).map((email: string) => {
    return { value: email, label: email }
  })

  const [addressBookList, setAddressBookList] = useState(
    uniqBy([...initialAddressBookList, ...recipientsList], 'value')
  )

  const hasAttachPdf = getFeature({ options, path: 'quotes.emails.attachPdf' })
  const hasReminders = getFeature({ options, path: 'quotes.emails.reminders' }) && template.name === 'quote-reminder'

  const showAttachPdf = hasAttachPdf && !template.name.includes('event')

  const objectLabels = getObjectLabels({ options })

  const allowDisable = typeof template.enabled !== 'undefined'

  const templateContext = getTemplateContext(editorPreference)
  const templateTags = getTemplateTags({
    options,
    invoice: template.name === 'send-invoice-client',
    event: template.name.includes('event-')
  })

  const reminderPeriodOptions = [
    { value: 2, label: '2 days' },
    { value: 3, label: '3 days' },
    { value: 5, label: '5 days' },
    { value: 7, label: '7 days' }
  ]

  const reminderRetryOptions = [
    { value: 1, label: '1' },
    { value: 2, label: '2' },
    { value: 3, label: '3' }
  ]

  const reminderStatusOptions = [
    { label: 'Estimate has been Viewed', value: 'viewed' },
    { label: 'Estimate has been Accepted or Declined', value: 'accepted' }
  ]

  const quoteTypeOptions = getQuoteTypeOptions({ options, hideAll: true })
    .map((type) => ({
      value: type.key,
      label: type.label
    }))
    .sort((a) => (a.value === 'all' ? -1 : 1))

  // Helper functions
  const handleImport = () => {
    openDialog(ImportJsonDialog, {
      onImport: (json: { [key: string]: any }) => {
        // Clean name on json import so our new key from template works
        const { name: _name, ...cleanJson } = json
        setTemplate({
          ...template,
          ...(cleanJson as unknown as EmailTemplate)
        })
        dismissDialog()
      },
      onClose: dismissDialog
    })
  }

  const handleExport = () => {
    openDialog(ExportJsonDialog, {
      item: template,
      onConfirm: dismissDialog
    })
  }

  const handleDefaultQuoteTypeChange = (values: string[]) => {
    setTemplate({
      ...template,
      defaultFor: values
    })
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setTemplate((template) => ({
      ...template,
      [name]: value
    }))
  }

  const handleAddressBookChange = (values: DropdownSelectOption[]) => {
    setRecipients(
      values.map((item: DropdownSelectOption) => {
        return { value: item.value, label: item.value }
      })
    )
    setTemplate({
      ...template,
      additionalRecipients: values.map((item: DropdownSelectOption) => item.value)
    })
  }

  const handleCheckboxChange = (name: string) => (event: any, value: boolean) => {
    setTemplate({
      ...template,
      [name]: value
    })
  }

  const handleReminderSelectChange = (name: string) => (value: DropdownSelectOption) => {
    setTemplate({
      ...template,
      [name]: value.value
    })
  }

  const handleSave = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (onConfirm) {
      onConfirm({
        ...template,
        subject: emailSubjectRef.current.getHTML(),
        body: emailBodyRef.current.getHTML()
      })
    }
  }

  const hasChanges = () => {
    const currentTemplate = {
      ...template,
      subject: emailSubjectRef.current?.getHTML() || template.subject,
      body: emailBodyRef.current?.getHTML() || template.body
    }
    return !isEqual(currentTemplate, initialTemplate.current)
  }

  const handleClose = () => {
    if (hasChanges()) {
      openDialog(ConfirmationDialog, {
        message: 'You have unsaved changes. Are you sure you want to close?',
        onConfirm: () => {
          dismissDialog() // Dismiss confirmation dialog
          if (onCancel) {
            onCancel()
          }
        },
        onCancel: () => {
          dismissDialog()
        }
      })
    } else {
      if (onCancel) {
        onCancel()
      }
    }
  }

  // Checkboxes
  const pdfCheckbox = <Checkbox checked={template.attachPdf} onChange={handleCheckboxChange('attachPdf')} />
  const reminderCheckbox = (
    <Checkbox checked={template.reminderDefault} onChange={handleCheckboxChange('reminderDefault')} />
  )

  // Buttons
  const rightButton = isAdminSite ? (
    <div className={classes.rightContentWrapper}>
      <SplitMenuButton
        onClick={handleImport}
        variant={'contained'}
        color={'primary'}
        disabled={loading}
        Icon={ArrowIcon}
        label={'Import'}
      >
        <MenuItem onClick={handleExport}>Export</MenuItem>
        {onEmailMigrate && (
          <MenuItem
            onClick={() =>
              onEmailMigrate({
                ...template,
                subject: emailSubjectRef.current.getHTML(),
                body: emailBodyRef.current.getHTML()
              })
            }
          >
            Move to Settings
          </MenuItem>
        )}
      </SplitMenuButton>
      <IconButton size="small" onClick={handleClose} disabled={loading}>
        <CloseIcon />
      </IconButton>
    </div>
  ) : (
    <IconButton size="small" onClick={handleClose} disabled={loading}>
      <CloseIcon />
    </IconButton>
  )

  return (
    <Dialog classes={{ root: classes.root }} maxWidth={'md'} fullWidth={true} onClose={handleClose} {...otherProps}>
      <DialogTitle classes={{ root: classes.dialogTitle }} loading={loading} rightContent={rightButton}>
        Edit Template
      </DialogTitle>
      <DialogContent>
        <form id="edit-template-dialog-form" onSubmit={handleSave}>
          {customTemplate && (
            <div>
              <InputField
                label="Title"
                value={template.label}
                fullWidth={true}
                name={'label'}
                onChange={handleChange}
                required
              />
            </div>
          )}
          {!toHidden && (
            <div className={customTemplate ? classes.templateField : ''}>
              <DropdownSelect
                value={recipients}
                options={addressBookList}
                label={'To'}
                sublabel={'Select email from address book or add new email'}
                searchable
                fullWidth
                variant="multi"
                onChange={handleAddressBookChange}
                noOptionsMessage={'Enter email address'}
                onCreateOption={(newOption: string) => {
                  const newAddressBookList = [...addressBookList, { value: newOption, label: newOption }]
                  setAddressBookList(newAddressBookList)
                  handleAddressBookChange([...recipients, { value: newOption, label: newOption }])
                }}
                popperModifiers={{
                  flip: {
                    enabled: false
                  },
                  preventOverflow: {
                    enabled: false
                  }
                }}
              />
            </div>
          )}

          <div className={customTemplate || !toHidden ? classes.templateField : ''}>
            <Editor
              label={'Subject'}
              singleLine={true}
              key={template.subject}
              content={template.subject}
              templateContext={templateContext}
              disabled={!canEdit}
              toolbar={{
                bold: false,
                italic: false,
                underline: false,
                color: false,
                highlight: false,

                lists: false,
                link: false,
                template: {
                  tags: templateTags
                }
              }}
              fullWidth={true}
              ref={emailSubjectRef}
            />
          </div>
          <div className={classes.templateField}>
            <Editor
              label={'Body'}
              key={template.body}
              content={template.body}
              templateContext={templateContext}
              toolbar={{
                color: false,
                highlight: false,
                template: {
                  tags: templateTags
                }
              }}
              fullWidth={true}
              disabled={!canEdit}
              ref={emailBodyRef}
            />
          </div>
          {showAttachPdf && (
            <div>
              <FormControl>
                <FormControlLabel
                  disabled={!canEdit}
                  control={pdfCheckbox}
                  label="Attach PDF"
                  style={{ marginRight: 0 }}
                />
              </FormControl>
            </div>
          )}
          {hasReminders && (
            <Grid container direction="column" spacing={2}>
              <Grid item>
                <FormControlLabel
                  disabled={!canEdit}
                  control={reminderCheckbox}
                  label="Send Reminder by Default"
                  style={{ marginRight: 0 }}
                />
              </Grid>
              <Grid item>
                <DropdownSelect
                  variant="single"
                  label={'Reminder Period'}
                  disabled={!canEdit}
                  fullWidth={true}
                  value={find(reminderPeriodOptions, { value: template.reminderPeriod })}
                  onChange={handleReminderSelectChange('reminderPeriod')}
                  options={reminderPeriodOptions}
                />
              </Grid>
              <Grid item>
                <DropdownSelect
                  variant="single"
                  label={'Number of Reminders'}
                  fullWidth={true}
                  disabled={!canEdit}
                  value={find(reminderRetryOptions, { value: template.reminderRetries })}
                  onChange={handleReminderSelectChange('reminderRetries')}
                  options={reminderRetryOptions}
                />
              </Grid>
              <Grid item>
                <DropdownSelect
                  variant="single"
                  label={'Send Reminders Until'}
                  fullWidth={true}
                  disabled={!canEdit}
                  value={find(reminderStatusOptions, { value: template.reminderStatus })}
                  onChange={handleReminderSelectChange('reminderStatus')}
                  options={reminderStatusOptions}
                />
              </Grid>
            </Grid>
          )}
          {customTemplate && (
            <div className={classes.templateField}>
              <DropdownSelect
                variant="multi"
                fullWidth
                label={`Use as Default for ${objectLabels.quote.value} Type(s)`}
                disabled={!canEdit}
                onChange={(options) => {
                  handleDefaultQuoteTypeChange(options.map((item) => item.value))
                }}
                options={quoteTypeOptions}
                value={quoteTypeOptions.filter((option) => template?.defaultFor?.includes(option.value))}
              />
            </div>
          )}
        </form>
      </DialogContent>
      <DialogActions
        showBorder={true}
        alignLeftButtonWithTitle={true}
        leftButton={
          allowDisable ? (
            <FormControlLabel
              className={classes.switchLabel}
              disabled={!canEdit}
              control={
                <ActiveSwitch color="primary" checked={template.enabled} onChange={handleCheckboxChange('enabled')} />
              }
              label="Enabled"
            />
          ) : undefined
        }
      >
        <Button disabled={loading} type="submit" form="edit-template-dialog-form" variant={'contained'}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EmailTemplateDialog
